import { hashObject, sha256 } from './identity';

export const ASSET_KEYS = ['baseEdid', 'baseDimming', 'baseTiming', 'baseCfg'];
const record = (value, keys, label) => {
  if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).sort().join('|') !== [...keys].sort().join('|')) throw new Error(`${label}: unknown or missing fields.`);
};
const finite = (value, low, high, label) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < low || value > high) throw new Error(`${label}: invalid finite value.`);
};
const text = (value, max, label) => {
  if (typeof value !== 'string' || !value.trim() || value.trim() !== value || value.length > max || !/^[ -~]+$/.test(value)) throw new Error(`${label}: printable ASCII without outer spaces is required.`);
};
const tuple = (value, length, label) => {
  if (!Array.isArray(value) || value.length !== length) throw new Error(`${label}: invalid number of values.`);
};
export function validateProfile(profile, { hardware = false } = {}) {
  record(profile, ['schemaVersion', 'id', 'revision', 'monitorType', 'target', 'gamma', 'timingNumber', 'station', 'limits', 'ecdis', 'assets', 'qualification', 'status'], 'Profile');
  if (profile.schemaVersion !== 'northstar-profile/1' || profile.status !== 'experimental') throw new Error('Choose an experimental northstar-profile/1 package.');
  for (const key of ['id', 'revision', 'monitorType']) text(profile[key], key === 'monitorType' ? 13 : 100, key);
  record(profile.target, ['x', 'y', 'luminance'], 'Target');
  finite(profile.target.x, .001, .999, 'Target x'); finite(profile.target.y, .001, .999, 'Target y');
  if (profile.target.x + profile.target.y >= 1) throw new Error('Invalid target chromaticity.');
  finite(profile.target.luminance, 1, 2000, 'Target luminance'); finite(profile.gamma, 1, 4, 'Gamma');
  finite(profile.timingNumber, 0, 4000, 'Timing number');
  if (!Number.isInteger(profile.timingNumber)) throw new Error('Timing number must be an integer.');
  record(profile.station, ['id', 'settleSeconds', 'backlightSettleSeconds', 'repeatabilityRelative', 'meterSettings', 'calibrationDue'], 'Station');
  for (const key of ['id', 'meterSettings', 'calibrationDue']) text(profile.station[key], 500, `Station ${key}`);
  const due = profile.station.calibrationDue;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(due) || !Number.isFinite(Date.parse(`${due}T00:00:00Z`)) || new Date(`${due}T00:00:00Z`).toISOString().slice(0, 10) !== due) throw new Error('Invalid calibration due date.');
  finite(profile.station.settleSeconds, .01, 60, 'Settle seconds'); finite(profile.station.backlightSettleSeconds, .01, 120, 'Backlight settle');
  finite(profile.station.repeatabilityRelative, .00001, .1, 'Repeatability');
  const limits = profile.limits;
  record(limits, ['whiteRelative', 'whiteDeltaUv', 'additiveRelative', 'minimumContrast', 'driftRelative', 'signalRange', 'gammaBands', 'primaryBounds'], 'Limits');
  for (const key of ['whiteRelative', 'whiteDeltaUv', 'additiveRelative', 'driftRelative']) finite(limits[key], .00001, .2, key);
  finite(limits.minimumContrast, 1, 1000000, 'Minimum contrast');
  tuple(limits.primaryBounds, 3, 'Primary bounds');
  for (const box of limits.primaryBounds) {
    record(box, ['x', 'y'], 'Primary bound');
    for (const axis of ['x', 'y']) { tuple(box[axis], 2, axis); finite(box[axis][0], .001, .999, axis); finite(box[axis][1], box[axis][0], .999, axis); }
  }
  tuple(limits.signalRange, 2, 'Signal range'); finite(limits.signalRange[0], .01, 10, 'Signal low'); finite(limits.signalRange[1], limits.signalRange[0], 10, 'Signal high');
  if (!Array.isArray(limits.gammaBands) || !limits.gammaBands.length || limits.gammaBands.length > 240) throw new Error('Gamma bands are required.');
  let next = 16;
  for (const band of limits.gammaBands) {
    record(band, ['from', 'to', 'relative'], 'Gamma band');
    if (!Number.isInteger(band.from) || !Number.isInteger(band.to) || band.from !== next || band.to < next || band.to > 255) throw new Error('Gamma bands must cover 16–255 without gaps.');
    finite(band.relative, .0001, .5, 'Gamma tolerance'); next = band.to + 1;
  }
  if (next !== 256) throw new Error('Gamma bands must reach 255.');
  if (!Array.isArray(profile.ecdis) || !profile.ecdis.length || profile.ecdis.length > 300) throw new Error('Explicit ECDIS tokens are required.');
  const names = new Set(), modes = new Set(), references = {};
  for (const token of profile.ecdis) {
    record(token, ['name', 'mode', 'brightness', 'rgb', 'xyY', 'referenceLuminance', 'deltaLimit', 'luminanceWindow'], 'ECDIS token');
    const key = `${token.mode}.${token.name}`;
    if (typeof token.name !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(token.name) || names.has(key) || !['Day', 'Dusk', 'Night'].includes(token.mode)) throw new Error('Invalid or duplicate ECDIS token.');
    names.add(key); modes.add(token.mode);
    if (!Number.isInteger(token.brightness) || token.brightness < 0 || token.brightness > 100) throw new Error('Invalid ECDIS brightness.');
    tuple(token.rgb, 3, 'RGB');
    if (token.rgb.some(value => !Number.isInteger(value) || value < 0 || value > 255)) throw new Error('Invalid ECDIS RGB.');
    tuple(token.xyY, 3, 'Nominal xyY'); finite(token.xyY[0], .0001, .999, 'Nominal x'); finite(token.xyY[1], .0001, .999, 'Nominal y');
    if (token.xyY[0] + token.xyY[1] >= 1) throw new Error('Invalid ECDIS chromaticity.');
    finite(token.xyY[2], .000001, 2000, 'Nominal Y'); finite(token.referenceLuminance, .000001, 2000, 'Reference luminance');
    if (references[token.mode] !== undefined && references[token.mode] !== token.referenceLuminance) throw new Error('ECDIS mode references differ.');
    references[token.mode] = token.referenceLuminance;
    finite(token.deltaLimit, .001, 100, 'Colour limit'); tuple(token.luminanceWindow, 2, 'Luminance window');
    finite(token.luminanceWindow[0], .01, 1, 'Window low'); finite(token.luminanceWindow[1], 1, 10, 'Window high');
  }
  if (modes.size !== 3) throw new Error('Day, Dusk and Night tokens are required.');
  record(profile.assets, ASSET_KEYS, 'Assets');
  for (const asset of Object.values(profile.assets)) {
    record(asset, ['name', 'sha256'], 'Asset'); text(asset.name, 200, 'Asset name');
    if (asset.name.trim() !== asset.name || /[\\/]/.test(asset.name) || ['.', '..'].includes(asset.name)) throw new Error('Asset names must be base filenames without paths or outer spaces.');
    if (!/^[a-f0-9]{64}$/.test(asset.sha256)) throw new Error('Each asset requires its SHA-256.');
  }
  record(profile.qualification, ['signalMapping', 'meterSettings', 'assets', 'ecdisTable'], 'Qualification');
  if (Object.values(profile.qualification).some(value => typeof value !== 'boolean')) throw new Error('Invalid qualification declarations.');
  if (hardware && (profile.id.toLowerCase() === 'synthetic-demo' || !Object.values(profile.qualification).every(Boolean))) throw new Error('Hardware needs an explicitly reviewed experimental profile and assets.');
  return profile;
}

export async function verifyPackage(profile, assets, { hardware = false } = {}) {
  validateProfile(profile, { hardware }); record(assets, ASSET_KEYS, 'Asset bytes');
  for (const key of ASSET_KEYS) {
    if (!(assets[key] instanceof Uint8Array) || !assets[key].length || assets[key].length > 256 * 1024) throw new Error(`Invalid or oversized ${key}.`);
    if (await sha256(assets[key]) !== profile.assets[key].sha256) throw new Error(`Asset hash mismatch: ${key}.`);
  }
  return { profile: JSON.parse(JSON.stringify(profile)), assets: Object.fromEntries(ASSET_KEYS.map(key => [key, assets[key].slice()])), profileHash: await hashObject(profile) };
}

export function parseProfileJson(source) {
  if (typeof source !== 'string' || source.length > 512 * 1024) throw new Error('Profile JSON exceeds 512 KiB.');
  const parsed = JSON.parse(source); let cursor = 0;
  const whitespace = () => { while (/\s/.test(source[cursor] || '') && cursor < source.length) cursor++; };
  const string = () => {
    const start = cursor++;
    while (cursor < source.length) {
      if (source[cursor] === '\\') cursor += 2;
      else if (source[cursor++] === '"') return JSON.parse(source.slice(start, cursor));
    }
    throw new Error('Invalid profile JSON string.');
  };
  const scan = depth => {
    if (depth > 30) throw new Error('Profile JSON is too deeply nested.');
    whitespace();
    if (source[cursor] === '{') {
      cursor++; whitespace(); const keys = new Set();
      while (source[cursor] !== '}') {
        const key = string(); if (keys.has(key)) throw new Error(`Duplicate profile JSON key: ${key}`); keys.add(key);
        whitespace(); cursor++; scan(depth + 1); whitespace();
        if (source[cursor] !== ',') break;
        cursor++; whitespace();
      }
      cursor++;
    } else if (source[cursor] === '[') {
      cursor++; whitespace();
      while (source[cursor] !== ']') { scan(depth + 1); whitespace(); if (source[cursor] !== ',') break; cursor++; }
      cursor++;
    } else if (source[cursor] === '"') string();
    else { while (cursor < source.length && !/[\s,\]}]/.test(source[cursor])) cursor++; }
  };
  scan(0); return parsed;
}

export { createDemoPackage } from './simulation';
